import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import shareFill from "@iconify/icons-eva/share-fill";
import twitterFill from "@iconify/icons-eva/twitter-fill";
import linkedinFill from "@iconify/icons-eva/linkedin-fill";
import facebookFill from "@iconify/icons-eva/facebook-fill";
import instagramFilled from "@iconify/icons-ant-design/instagram-filled";
import {
  Box,
  Card,
  Divider,
  Skeleton,
  Container,
  Typography,
  Grid,
  Avatar,
  SpeedDial,
  useTheme,
  useMediaQuery,
  SpeedDialAction,
} from "@mui/material";

import { BlogState, Post } from "../types/blog";
import Markdown from "../components/Markdown";
import { BlogPostHero, BlogPostTags, BlogPostRecent } from "../components/blog";
import { useBlogStore } from "../store/blogStore";
import AdditionalInfo from "../components/ProductDetail/AdditionalInfo";
import BookingModal from "../components/BookingModal";
import { fDate } from "../utils/formatTime";
import { useParams } from "react-router-dom";
import SimilarProducts from "../components/ProductDetail/SimilarProducts";
import { useProductStore } from "../store/productStore";

const SkeletonLoad = (
  <>
    <Skeleton
      width="100%"
      height={560}
      variant="rectangular"
      sx={{ borderRadius: 2 }}
    />
    <Box sx={{ mt: 3, display: "flex", alignItems: "center" }}>
      <Skeleton variant="circular" width={64} height={64} />
      <Box sx={{ flexGrow: 1, ml: 2 }}>
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} />
      </Box>
    </Box>
  </>
);

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();

  const [openBooking, setOpenBooking] = useState(false);
  const { posts } = useBlogStore();
  const { products } = useProductStore();

  const recentPosts = posts.slice(0, 4);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const post = slug
    ? posts.find(
        (p) => p.slug?.trim().toLowerCase() === slug.trim().toLowerCase()
      )
    : undefined;

  const handleOpenBooking = () => setOpenBooking(true);
  const handleCloseBooking = () => setOpenBooking(false);

  const SOCIALS = [
    {
      name: "Facebook",
      icon: <Icon icon={facebookFill} width={20} height={20} color="#1877F2" />,
    },
    {
      name: "Instagram",
      icon: (
        <Icon icon={instagramFilled} width={20} height={20} color="#D7336D" />
      ),
    },
    {
      name: "Linkedin",
      icon: <Icon icon={linkedinFill} width={20} height={20} color="#006097" />,
    },
    {
      name: "Twitter",
      icon: <Icon icon={twitterFill} width={20} height={20} color="#1C9CEA" />,
    },
  ];

  if (!slug) return <Container>Không tìm thấy đường dẫn bài viết.</Container>;
  if (!post) return <Container>{SkeletonLoad}</Container>;

  return (
    <Container maxWidth="xl" className="pt-3">
      <Box className="lg:flex md:space-x-5">
        <Card sx={{ width: { xs: "100%", md: "62%" } }}>
          <Box sx={{ position: "relative" }}>
            {post.cover && (
              <img alt="post cover" src={post.cover} className="px-6" />
            )}
            <Typography variant="h5" className="px-6 py-3">
              {post.title}
            </Typography>

            <Box className="flex flex-row justify-between px-6">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  alt={post.author?.name || "Tác giả"}
                  // src={post.author?.avatarUrl}
                  src="https://firebasestorage.googleapis.com/v0/b/refrigeration-9e407.firebasestorage.app/o/avatar%2Favatar.webp?alt=media&token=ee0c08d9-8d64-4937-9e2d-6203c00c1c58"
                  sx={{ width: 48, height: 48 }}
                />
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1">Kim Chung</Typography>
                  <Typography variant="body2" sx={{ color: "grey.500" }}>
                    {fDate(post.createdAt)}
                  </Typography>
                </Box>
              </Box>

              <SpeedDial
                direction={isMobile ? "up" : "left"}
                ariaLabel="Share post"
                icon={<Icon icon={shareFill} />}
                sx={{ "& .MuiSpeedDial-fab": { width: 48, height: 48 } }}
              >
                {SOCIALS.map((action) => (
                  <SpeedDialAction
                    key={action.name}
                    icon={action.icon}
                    tooltipTitle={action.name}
                    tooltipPlacement="top"
                    FabProps={{ color: "default" }}
                  />
                ))}
              </SpeedDial>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 3, md: 3 } }}>
            {post.description && (
              <Typography variant="h6" sx={{ mb: 1 }}>
                {post.description}
              </Typography>
            )}

            {/* ✅ Dùng Markdown để render nội dung */}
            {post.body && <Markdown children={post.body} />}

            <Box sx={{ my: 2 }}>
              <Divider />
              <BlogPostTags post={post} />
            </Box>
          </Box>
        </Card>

        {/* Sidebar */}
        <Grid
          size={{ xs: 12, md: 5 }}
          sx={{
            width: { xs: "100%", md: "38%" },
            paddingTop: { xs: "20px", lg: "0px" },
            marginLeft: { xs: "0px !important", lg: "10px !important" },
          }}
          className="relative"
        >
          <div className="bg-white rounded p-3 shadow-md">
            <img src="/images/voucher.png" alt="voucher" className="pb-5" />
            <Divider sx={{ my: 2 }} />
            <Box className="booking-button flex items-center justify-center">
              <button
                className="bg-orange-500 text-white px-4 py-4 font-bold shadow-md rounded-md w-3/4 hover:bg-orange-500/90 transition hover:shadow-lg"
                onClick={handleOpenBooking}
              >
                LIÊN HỆ NGAY
              </button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <AdditionalInfo />
          </div>

          {recentPosts.length > 0 && (
            <Box className="bg-white rounded mt-3 p-3 shadow-md">
              <BlogPostRecent posts={recentPosts} />
            </Box>
          )}
        </Grid>
      </Box>

      <SimilarProducts product={products[0]} isShowFullScreen={true} />

      {/* Booking Modal */}
      <BookingModal
        open={openBooking}
        handleClose={handleCloseBooking}
        serviceName=""
      />
    </Container>
  );
}
